// src/types/assistant/state.ts
// UI State Types for Multi-Agent AI Assistant

import type { AgentId, UIGroup } from "./events";
import type { ChartSpec } from "./charts";

// ─────────────────────────────────────────────────────────────────────────────
// Agent Status
// ─────────────────────────────────────────────────────────────────────────────

export type AgentStatus = "pending" | "working" | "done" | "irrelevant" | "error";

// ─────────────────────────────────────────────────────────────────────────────
// Tool State
// ─────────────────────────────────────────────────────────────────────────────

export interface ToolState {
  id: string;
  name: string;
  phase: "calling" | "completed";
  elapsedMs?: number | undefined;
  ok?: boolean | undefined;
  argsPreview?: string | undefined;
  resultPreview?: string | undefined;
  payload?: Record<string, unknown> | undefined;
  server?: string | undefined;
  url?: string | undefined;
}

// ─────────────────────────────────────────────────────────────────────────────
// Agent State
// ─────────────────────────────────────────────────────────────────────────────

export interface AgentState {
  id: AgentId;
  status: AgentStatus;
  displayName: string;
  uiGroup: UIGroup;
  tokenBuffer: string;       // Accumulated draft tokens
  draftText: string;         // Final draft text (from agent.draft)
  thinkingText: string;      // Accumulated thinking text
  activeTools: Map<string, ToolState>;
  completedTools: ToolState[];
  error?: string | undefined;
}

// ─────────────────────────────────────────────────────────────────────────────
// Multi-Agent Stream State
// ─────────────────────────────────────────────────────────────────────────────

export interface MultiAgentStreamState {
  active: boolean;
  conversationId: string | null;
  plannedAgents: AgentId[];
  agents: Partial<Record<AgentId, AgentState>>;
  charts: Map<string, ChartSpec>;  // Chart specs indexed by id
  finalOutput: string;             // Synthesizer tokens accumulate here
  finalMessageText: string;        // Complete text from message event
}

// ─────────────────────────────────────────────────────────────────────────────
// Helper Functions
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Get default display name for an agent
 */
export function getDefaultDisplayName(id: AgentId): string {
  const names: Record<AgentId, string> = {
    data: "Data Research",
    search: "Search",
    chart: "Charts",
    synthesizer: "Report Synthesis",
    quick_chat: "Quick Chat",
  };
  return names[id] ?? id;
}

/**
 * Get default UI group for an agent
 */
export function getDefaultUIGroup(id: AgentId): UIGroup {
  const groups: Record<AgentId, UIGroup> = {
    data: "analysis",
    search: "analysis",
    chart: "render",
    synthesizer: "synthesis",
    quick_chat: "chat",
  };
  return groups[id] ?? "analysis";
}

/**
 * Create initial state for a single agent
 */
export function createInitialAgentState(id: AgentId, displayName?: string, uiGroup?: UIGroup): AgentState {
  return {
    id,
    status: "pending",
    displayName: displayName ?? getDefaultDisplayName(id),
    uiGroup: uiGroup ?? getDefaultUIGroup(id),
    tokenBuffer: "",
    draftText: "",
    thinkingText: "",
    activeTools: new Map(),
    completedTools: [],
  };
}

/**
 * Create initial multi-agent stream state
 */
export function createInitialMultiAgentState(): MultiAgentStreamState {
  return {
    active: false,
    conversationId: null,
    plannedAgents: [],
    agents: {},
    charts: new Map(),
    finalOutput: "",
    finalMessageText: "",
  };
}

